import React, { useState, useEffect, useCallback } from 'react';
import {
  CheckCircle2, XCircle, Shield, ChevronDown, ChevronRight,
  Monitor, Smartphone, Tablet, Globe, Clock, MapPin,
} from 'lucide-react';

// ---------------------------------------------------------------------------
// Types — mirrors AuthAuditEntry from system-api (serialised over the wire)
// ---------------------------------------------------------------------------

export interface AuthAuditEntryClient {
  _id: string;
  event: string;
  severity: 'info' | 'warn' | 'error';
  timestamp: string;
  userId?: string;
  userEmail?: string;
  userName?: string;
  ipAddress?: string;
  userAgent?: string;
  deviceId?: string;
  departmentId?: string;
  details?: {
    portal?: string;
    departmentSlug?: string;
    signInType?: string;
    issuer?: string;
    reason?: string;
    browser?: string;
    browserVersion?: string;
    os?: string;
    osVersion?: string;
    deviceType?: 'desktop' | 'mobile' | 'tablet';
    screenWidth?: number;
    screenHeight?: number;
    timezone?: string;
    language?: string;
    [key: string]: unknown;
  };
  errorMessage?: string;
  errorCode?: string;
}

export interface LoginHistoryResult {
  data: AuthAuditEntryClient[];
  total: number;
  limit: number;
  skip: number;
}

export interface LoginHistoryTabProps {
  userId: string;
  fetchFn: (
    userId: string,
    opts?: { limit?: number; skip?: number; event?: string },
  ) => Promise<LoginHistoryResult>;
  active: boolean;
}

// ---------------------------------------------------------------------------
// Filter pill type
// ---------------------------------------------------------------------------

type FilterEvent = 'all' | 'login_success' | 'login_failure';

const FILTER_PILLS: { id: FilterEvent; label: string }[] = [
  { id: 'all', label: 'All' },
  { id: 'login_success', label: 'Successful' },
  { id: 'login_failure', label: 'Failed' },
];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function relativeTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(iso).toLocaleDateString();
}

function portalLabel(portal?: string): string {
  if (portal === 'system-web') return 'System';
  if (portal === 'tenant-web') return 'Department';
  if (portal === 'mobile') return 'Mobile';
  return portal || 'Unknown';
}

function portalColor(portal?: string): string {
  if (portal === 'system-web') return 'bg-orange-100 text-orange-700';
  if (portal === 'tenant-web') return 'bg-blue-100 text-blue-700';
  if (portal === 'mobile') return 'bg-green-100 text-green-700';
  return 'bg-gray-100 text-gray-600';
}

function denialLabel(reason?: string): string {
  switch (reason) {
    case 'USER_NOT_FOUND': return 'Account not found';
    case 'NOT_SYSTEM_ADMIN': return 'No system portal access';
    case 'ACCOUNT_INACTIVE': return 'Account inactive';
    case 'NO_DEPARTMENT_ACCESS': return 'No department membership';
    case 'DEPT_NOT_FOUND': return 'Department not found';
    default: return reason || 'Access denied';
  }
}

function DeviceIcon({ deviceType }: { deviceType?: string }) {
  if (deviceType === 'mobile') return <Smartphone className="h-3.5 w-3.5 text-gray-400" />;
  if (deviceType === 'tablet') return <Tablet className="h-3.5 w-3.5 text-gray-400" />;
  return <Monitor className="h-3.5 w-3.5 text-gray-400" />;
}

function EventStatusIcon({ event }: { event: string }) {
  if (event === 'login_success') {
    return <CheckCircle2 className="h-4 w-4 text-green-500 shrink-0" />;
  }
  if (event === 'login_failure') {
    return <XCircle className="h-4 w-4 text-red-500 shrink-0" />;
  }
  return <Shield className="h-4 w-4 text-gray-400 shrink-0" />;
}

// ---------------------------------------------------------------------------
// Expanded detail card
// ---------------------------------------------------------------------------

function LoginDetailCard({ entry }: { entry: AuthAuditEntryClient }) {
  const d = entry.details || {};
  const rows: { label: string; value: string }[] = [];

  if (entry.timestamp) {
    rows.push({ label: 'Date / Time', value: new Date(entry.timestamp).toLocaleString() });
  }
  if (d.portal) rows.push({ label: 'Portal', value: portalLabel(d.portal) });
  if (d.departmentSlug) rows.push({ label: 'Department', value: d.departmentSlug });
  if (d.reason) rows.push({ label: 'Denial Reason', value: denialLabel(d.reason) });
  if (d.os) {
    rows.push({ label: 'Operating System', value: d.osVersion ? `${d.os} ${d.osVersion}` : d.os });
  }
  if (d.browser) {
    rows.push({ label: 'Browser', value: d.browserVersion ? `${d.browser} ${d.browserVersion}` : d.browser });
  }
  if (d.deviceType) rows.push({ label: 'Device Type', value: d.deviceType });
  if (d.screenWidth && d.screenHeight) {
    rows.push({ label: 'Screen', value: `${d.screenWidth} × ${d.screenHeight}` });
  }
  if (d.timezone) rows.push({ label: 'Timezone', value: d.timezone });
  if (d.language) rows.push({ label: 'Language', value: d.language });
  if (entry.ipAddress) rows.push({ label: 'IP Address', value: entry.ipAddress });
  if (d.signInType) rows.push({ label: 'Sign-in Type', value: d.signInType });
  if (d.issuer) rows.push({ label: 'Auth Issuer', value: d.issuer });
  if (entry.userAgent) rows.push({ label: 'User Agent', value: entry.userAgent });

  return (
    <div className="bg-gray-50 border-t border-gray-200 px-4 py-3">
      <div className="grid grid-cols-2 gap-x-4 gap-y-1.5">
        {rows.map((r) => (
          <div key={r.label} className="col-span-2 sm:col-span-1">
            <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-400">{r.label}</p>
            <p className="text-xs text-gray-700 break-all">{r.value}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Login row
// ---------------------------------------------------------------------------

function LoginRow({
  entry,
  isExpanded,
  onToggle,
}: {
  entry: AuthAuditEntryClient;
  isExpanded: boolean;
  onToggle: () => void;
}) {
  const d = entry.details || {};
  const osName = d.os || '';
  const browserName = d.browser || '';
  const deviceSummary = [osName, browserName].filter(Boolean).join(' · ') || 'Unknown device';

  return (
    <>
      <button
        onClick={onToggle}
        className={`w-full text-left px-4 py-3 flex items-center gap-3 hover:bg-gray-50 transition-colors border-b border-gray-100 ${
          isExpanded ? 'bg-gray-50' : 'bg-white'
        }`}
      >
        <EventStatusIcon event={entry.event} />

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs font-medium text-gray-900">
              {entry.event === 'login_success' ? 'Successful login' : 'Failed login'}
            </span>
            {d.portal && (
              <span className={`inline-flex px-1.5 py-0.5 text-[10px] font-medium rounded-full ${portalColor(d.portal)}`}>
                {portalLabel(d.portal)}
              </span>
            )}
            {entry.event === 'login_failure' && d.reason && (
              <span className="text-[10px] text-red-500 font-medium">{denialLabel(d.reason)}</span>
            )}
          </div>
          <div className="flex items-center gap-2 mt-0.5 text-[11px] text-gray-400 flex-wrap">
            <span className="flex items-center gap-1">
              <DeviceIcon deviceType={d.deviceType} />
              {deviceSummary}
            </span>
            {entry.ipAddress && (
              <span className="flex items-center gap-1">
                <Globe className="h-3 w-3" />
                {entry.ipAddress}
              </span>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2 shrink-0">
          <span
            className="text-[11px] text-gray-400"
            title={new Date(entry.timestamp).toLocaleString()}
          >
            {relativeTime(entry.timestamp)}
          </span>
          {isExpanded
            ? <ChevronDown className="h-3.5 w-3.5 text-gray-400" />
            : <ChevronRight className="h-3.5 w-3.5 text-gray-400" />
          }
        </div>
      </button>

      {isExpanded && <LoginDetailCard entry={entry} />}
    </>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

const PAGE_SIZE = 20;

export default function LoginHistoryTab({ userId, fetchFn, active }: LoginHistoryTabProps) {
  const [filter, setFilter] = useState<FilterEvent>('all');
  const [entries, setEntries] = useState<AuthAuditEntryClient[]>([]);
  const [total, setTotal] = useState(0);
  const [skip, setSkip] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const load = useCallback(
    async (newSkip: number, newFilter: FilterEvent, append: boolean) => {
      if (newSkip === 0) setLoading(true);
      else setLoadingMore(true);
      try {
        const opts = {
          limit: PAGE_SIZE,
          skip: newSkip,
          event: newFilter !== 'all' ? newFilter : undefined,
        };
        const result = await fetchFn(userId, opts);
        if (append) {
          setEntries((prev) => [...prev, ...result.data]);
        } else {
          setEntries(result.data);
          setExpandedId(null);
        }
        setTotal(result.total);
        setSkip(newSkip + result.data.length);
      } catch {
        // non-fatal
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [fetchFn, userId],
  );

  useEffect(() => {
    if (!active) return;
    setSkip(0);
    load(0, filter, false);
  }, [active, filter, userId, load]);

  function handleFilterChange(f: FilterEvent) {
    if (f === filter) return;
    setFilter(f);
    setSkip(0);
  }

  function handleToggle(id: string) {
    setExpandedId((prev) => (prev === id ? null : id));
  }

  const hasMore = entries.length < total;

  return (
    <div className="flex flex-col h-full">
      {/* Filter pills */}
      <div className="flex gap-1.5 px-4 py-2.5 border-b border-gray-200 shrink-0 flex-wrap">
        {FILTER_PILLS.map((p) => (
          <button
            key={p.id}
            onClick={() => handleFilterChange(p.id)}
            className={`px-2.5 py-1 text-[11px] font-medium rounded-full transition-colors ${
              filter === p.id
                ? 'bg-orange-500 text-white'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            {p.label}
          </button>
        ))}
      </div>

      {/* Content */}
      <div className="flex-1 min-h-0 overflow-auto">
        {loading && (
          <div className="flex items-center justify-center py-8">
            <div className="h-5 w-5 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" />
          </div>
        )}

        {!loading && entries.length === 0 && (
          <div className="flex flex-col items-center justify-center py-10 text-center px-4">
            <Clock className="h-8 w-8 text-gray-300 mb-2" />
            <p className="text-sm font-medium text-gray-500">No login history recorded yet</p>
            <p className="text-xs text-gray-400 mt-1">
              Login events are captured from this point forward
            </p>
          </div>
        )}

        {!loading && entries.length > 0 && (
          <div>
            {entries.map((entry) => (
              <LoginRow
                key={entry._id}
                entry={entry}
                isExpanded={expandedId === entry._id}
                onToggle={() => handleToggle(entry._id)}
              />
            ))}

            {hasMore && (
              <div className="px-4 py-3 border-t border-gray-100">
                <button
                  onClick={() => load(skip, filter, true)}
                  disabled={loadingMore}
                  className="w-full text-xs text-orange-600 hover:text-orange-700 font-medium py-1.5 flex items-center justify-center gap-2 disabled:opacity-50"
                >
                  {loadingMore ? (
                    <div className="h-3.5 w-3.5 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" />
                  ) : (
                    `Load more (${total - entries.length} remaining)`
                  )}
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Summary footer */}
      {!loading && entries.length > 0 && (
        <div className="shrink-0 px-4 py-2 border-t border-gray-100 flex items-center gap-1.5">
          <MapPin className="h-3 w-3 text-gray-300" />
          <span className="text-[10px] text-gray-400">
            Showing {entries.length} of {total} event{total !== 1 ? 's' : ''}
          </span>
        </div>
      )}
    </div>
  );
}
